import { getReorderedArray } from 'utils';
import { defaultContextValues, IAction, IState } from './UseOffsiteCampaignEditorContext';
import { useFeatureFlags } from 'core/app/AppContext';

export type ActionTypes =
  | 'ADD_SEGMENT'
  | 'ADD_SEGMENT_FILTER'
  | 'ADD_CYCLE'
  | 'ADD_UNSAVED_SEGMENT'
  | 'CLEAR_ERROR'
  | 'DISCARD_CHANGES'
  | 'REMOVE_CYCLE'
  | 'REMOVE_SEGMENT_FILTER'
  | 'REMOVE_SEGMENT'
  | 'REMOVE_UNSAVED_SEGMENT'
  | 'REORDER_SEGMENTS'
  | 'RESET_EDITOR'
  | 'SAVE_CHANGES'
  | 'SET_CAMPAIGN_END_TIME'
  | 'SET_CAMPAIGN_SPLIT_TEST_END_TIME'
  | 'SET_CAMPAIGN_INITIAL'
  | 'SET_CAMPAIGN_NAME'
  | 'SET_CAMPAIGN_START_TIME'
  | 'SET_CAMPAIGN_SPLIT_TEST_START_TIME'
  | 'UPDATE_SEGMENT_NAME'
  | 'UPDATE_SPLIT_TEST_NAME'
  | 'SET_ACTIVE_CHANNELS'
  | 'SET_CAMPAIGN_STATE'
  | 'SET_HAS_MISSING_FIELDS'
  | 'SET_MINIMUM_BROWSE_TIME'
  | 'SET_SITE_INITIAL'
  | 'SET_TEMPLATES'
  | 'SET_UNSUBSCRIBE_TEMPLATES'
  | 'SET_GLOBAL_UNSUBSCRIBE_TEMPLATES'
  | 'SET_UNSAVED_CAMPAIGN_CHANGES'
  | 'UPDATE_SEGMENT_CUSTOM_FILTERS'
  | 'UPDATE_SEGMENT_ID'
  | 'UPDATE_CYCLE'
  | 'UPDATE_SPLIT_TESTS'
  | 'UPDATE_SPLIT_TEST_ID'
  | 'UPDATE_TESTING_FILTER_LIST'
  | 'UPDATE_SPLIT_NAME'
  | 'UPDATE_SPLIT_PERCENTAGE'
  | 'SET_CAMPAIGN_SUPPRESSION_TIME'
  | 'REMOVE_SUPPRESSION_TIME'
  | 'SET_ORIGINAL_CAMPAIGN_STATE'
  | 'UPDATE_LOOKBACK_PERIOD';

export function OffsiteCampaignEditorReducer(state: IState, action: IAction) {
  const { payload } = action;
  let newState: IState = null;
  const { flagEnabled } = useFeatureFlags();
  const featureControlGroup = flagEnabled('ld-control-group');

  const showControlGroupName = (cycles, index, oldName) => {
    if (featureControlGroup) {
      // Only change the name if the feature flag is on

      // If it's a control group, change the name to control group
      if (cycles.length === 0) {
        return 'Control Group';
      } else {
        // Otherwise give it a generic name
        return `Variant ${String.fromCharCode(65 + index)}`;
      }

      // If the flag is off, just use the old name
    } else return oldName;
  };

  switch (action.type) {
    case 'ADD_SEGMENT':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: [...state.campaign.segments, payload.segment],
        },
      };
      break;
    case 'ADD_SEGMENT_FILTER':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.segmentId
              ? { ...segment, segmentFilters: [...segment.segmentFilters, payload.segmentFilter] }
              : segment,
          ),
        },
      };
      break;
    case 'UPDATE_SEGMENT_CUSTOM_FILTERS':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.segmentId
              ? {
                  ...segment,
                  segmentFilters: [...payload.filters, ...segment.segmentFilters.filter(f => f.type !== 'custom')],
                }
              : segment,
          ),
        },
      };
      break;
    case 'ADD_CYCLE':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.segmentId
              ? {
                  ...segment,
                  percentageSplits: segment.percentageSplits.map((split, index) =>
                    split.id === payload.splitId
                      ? {
                          ...split,
                          name: showControlGroupName([...split.cycles, payload.cycle], index, split.name),
                          cycles: [...split.cycles, payload.cycle],
                        }
                      : {
                          ...split,
                          name: showControlGroupName(split.cycles, index, split.name),
                        },
                  ),
                }
              : segment,
          ),
        },
      };
      break;
    case 'CLEAR_ERROR':
      newState = {
        ...state,
      };
      break;
    case 'DISCARD_CHANGES':
      newState = {
        ...state,
        campaign: state.originalCampaign,
        hasUnsavedCampaignChange: false,
        unsavedSegmentChanges: [],
      };
      break;
    case 'REMOVE_SEGMENT':
      newState = {
        ...state,
        hasUnsavedCampaignChange: true,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.filter(segment => segment.id !== payload.segmentId),
        },
      };
      break;
    case 'REMOVE_CYCLE':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.segmentId
              ? {
                  ...segment,
                  percentageSplits: segment.percentageSplits.map((split, index) =>
                    split.id === payload.splitId
                      ? {
                          ...split,
                          name: showControlGroupName(
                            split.cycles.filter(cycle => cycle.id !== payload.cycleId),
                            index,
                            split.name,
                          ),
                          cycles: split.cycles.filter(cycle => cycle.id !== payload.cycleId),
                        }
                      : {
                          ...split,
                          name: showControlGroupName(split.cycles, index, split.name),
                        },
                  ),
                }
              : segment,
          ),
        },
      };
      break;
    case 'REMOVE_SEGMENT_FILTER':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.segmentId
              ? {
                  ...segment,
                  segmentFilters: segment.segmentFilters.filter(filter => filter.id !== payload.segmentFilterId),
                }
              : segment,
          ),
        },
      };
      break;
    case 'REORDER_SEGMENTS':
      newState = {
        ...state,
        hasOrderChange: true,
        campaign: {
          segments: getReorderedArray(state.campaign.segments, payload.oldIndex, payload.newIndex),
          ...state.campaign,
        },
      };
      break;
    case 'RESET_EDITOR':
      newState = { ...defaultContextValues };
      break;
    case 'SAVE_CHANGES':
      newState = {
        ...state,
        originalCampaign: state.campaign,
        hasUnsavedCampaignChange: false,
        unsavedSegmentChanges: [],
        hasOrderChange: false,
      };
      break;
    case 'SET_CAMPAIGN_END_TIME':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          endTime: payload.endTime,
        },
      };
      break;
    case 'SET_ACTIVE_CHANNELS':
      newState = {
        ...state,
        activeChannels: payload,
      };
      break;
    case 'SET_CAMPAIGN_SPLIT_TEST_END_TIME':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          splitTestEndDate: payload.splitTestEndDate,
        },
      };
      break;
    case 'SET_CAMPAIGN_INITIAL':
      newState = {
        ...state,
        campaign: payload.campaign,
        originalCampaign: payload.campaign,
      };
      break;
    case 'SET_CAMPAIGN_NAME':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          name: payload.name,
        },
      };
      break;
    case 'SET_CAMPAIGN_START_TIME':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          startTime: payload.startTime,
        },
      };
      break;
    case 'SET_CAMPAIGN_SPLIT_TEST_START_TIME':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          splitTestStartDate: payload.splitTestStartDate,
        },
      };
      break;
    case 'SET_CAMPAIGN_STATE':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          state: payload.state,
        },
      };
      break;
    case 'SET_HAS_MISSING_FIELDS':
      newState = {
        ...state,
        hasMissingFields: payload.hasMissingFields,
      };
      break;
    case 'SET_SITE_INITIAL':
      newState = {
        ...state,
        site: payload.site,
      };
      break;
    case 'SET_TEMPLATES':
      newState = {
        ...state,
        templates: payload.templates,
      };
      break;
    case 'SET_MINIMUM_BROWSE_TIME':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          minimumBrowseTime: payload.minimumBrowseTime,
        },
      };
      break;
    case 'SET_UNSUBSCRIBE_TEMPLATES':
      newState = {
        ...state,
        unsubscribeTemplates: payload.templates,
      };
      break;
    case 'SET_GLOBAL_UNSUBSCRIBE_TEMPLATES':
      newState = {
        ...state,
        unsubscribeTemplates: payload.templates,
      };
      break;
    case 'SET_UNSAVED_CAMPAIGN_CHANGES':
      newState = {
        ...state,
        hasUnsavedCampaignChange: payload.hasUnsavedCampaignChange,
      };
      break;
    case 'UPDATE_SEGMENT_NAME':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.segmentId ? { ...segment, name: payload.name } : segment,
          ),
        },
      };
      break;
    case 'UPDATE_SPLIT_TEST_NAME':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.segmentId ? { ...segment, splitTestName: payload.name } : segment,
          ),
        },
      };
      break;
    case 'UPDATE_SEGMENT_ID':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.originalSegmentId ? { ...segment, id: payload.newSegmentId } : segment,
          ),
        },
      };
      break;
    case 'UPDATE_SPLIT_TEST_ID':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.segmentId ? { ...segment, splitTestId: payload.splitTestId } : segment,
          ),
        },
      };
      break;
    case 'UPDATE_CYCLE':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.segmentId
              ? {
                  ...segment,
                  percentageSplits: segment.percentageSplits.map(split =>
                    split.id === payload.splitId
                      ? {
                          ...split,
                          cycles: split.cycles.map(cycle => (cycle.id === payload.cycleId ? payload.cycle : cycle)),
                        }
                      : split,
                  ),
                }
              : segment,
          ),
        },
      };
      break;
    case 'UPDATE_SPLIT_TESTS':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.segmentId
              ? {
                  ...segment,
                  percentageSplits: payload.updatedPercentageSplits.map((split, index) => ({
                    ...split,
                    name: showControlGroupName(split.cycles, index, split.name),
                  })),
                }
              : segment,
          ),
        },
      };
      break;
    case 'UPDATE_TESTING_FILTER_LIST':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.segmentId
              ? {
                  ...segment,
                  segmentFilters: segment.segmentFilters.map(filter =>
                    filter.id === payload.segmentFilterId
                      ? {
                          ...filter,
                          options: payload.options,
                        }
                      : filter,
                  ),
                }
              : segment,
          ),
        },
      };
      break;
    case 'UPDATE_SPLIT_NAME':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.segmentId
              ? {
                  ...segment,
                  percentageSplits: segment.percentageSplits.map(split =>
                    split.id === payload.splitId
                      ? {
                          ...split,
                          name: payload.splitName,
                        }
                      : split,
                  ),
                }
              : segment,
          ),
        },
      };
      break;
    case 'UPDATE_SPLIT_PERCENTAGE':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          segments: state.campaign.segments.map(segment =>
            segment.id === payload.segmentId
              ? {
                  ...segment,
                  percentageSplits: segment.percentageSplits.map(split =>
                    split.id === payload.splitId
                      ? {
                          ...split,
                          percentage: payload.percentage,
                        }
                      : split,
                  ),
                }
              : segment,
          ),
        },
      };
      break;
    case 'SET_CAMPAIGN_SUPPRESSION_TIME':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          suppressTimeWindows: payload.suppressTimeWindows,
        },
      };
      break;
    case 'REMOVE_SUPPRESSION_TIME':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          suppressTimeWindows: [],
        },
      };
      break;
    case 'UPDATE_LOOKBACK_PERIOD':
      newState = {
        ...state,
        campaign: {
          ...state.campaign,
          lookbackPeriod: payload.lookbackPeriod,
        },
      };
      break;
    case 'SET_ORIGINAL_CAMPAIGN_STATE':
      newState = {
        ...state,
        originalCampaign: payload,
      };
      break;
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
  return newState;
}
